package br.com.htms.inspeccaocampo.ui.kit

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectVerticalDragGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material3.Icon
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInRoot
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.ui.theme.PortalCores
import kotlinx.coroutines.launch
import kotlin.math.abs
import kotlin.math.max
import kotlin.math.roundToInt

/**
 * Área rolável com barra de rolagem arrastável e botões "topo"/"fim".
 * O conteúdo deve usar `Modifier.verticalScroll(state)` com o mesmo [state].
 */
@Composable
fun AreaRolavel(
    state: ScrollState,
    modifier: Modifier = Modifier,
    botoes: Boolean = true,
    content: @Composable BoxScope.() -> Unit
) {
    var altura by remember { mutableIntStateOf(0) }
    val ancora = remember { AncoraRolagem() }
    Box(
        modifier
            .onGloballyPositioned {
                val y = it.positionInRoot().y
                if (abs(y - ancora.topo) > 0.5f) ancora.topo = y
            }
            .onSizeChanged { if (it.height != altura) altura = it.height }
    ) {
        CompositionLocalProvider(
            LocalAncoraRolagem provides ancora,
            LocalRolagemPainel provides state
        ) {
            content()
        }
        ControlesScroll(state, altura, botoes)
    }
}

/** Mesma barra e botões para `LazyColumn(state = state)`. */
@Composable
fun AreaRolavel(
    state: LazyListState,
    modifier: Modifier = Modifier,
    botoes: Boolean = true,
    content: @Composable BoxScope.() -> Unit
) {
    Box(modifier) {
        content()
        ControlesLazy(state, botoes)
    }
}

@Composable
private fun BoxScope.ControlesScroll(state: ScrollState, altura: Int, botoes: Boolean) {
    val max = state.maxValue
    if (altura <= 0 || max <= 0 || max == Int.MAX_VALUE) return
    val scope = rememberCoroutineScope()
    val viewport = altura.toFloat()
    BarraRolagem(
        total = max + viewport,
        viewport = viewport,
        inicio = state.value.toFloat(),
        rolar = { state.dispatchRawDelta(it) }
    )
    if (botoes && max > altura / 2) {
        BotoesTopoFim(
            podeSubir = state.canScrollBackward,
            podeDescer = state.canScrollForward,
            onTopo = { scope.launch { state.animateScrollTo(0) } },
            onFim = { scope.launch { state.animateScrollTo(state.maxValue) } }
        )
    }
}

@Composable
private fun BoxScope.ControlesLazy(state: LazyListState, botoes: Boolean) {
    val info = state.layoutInfo
    val visiveis = info.visibleItemsInfo
    val n = info.totalItemsCount
    val viewport = (info.viewportEndOffset - info.viewportStartOffset).toFloat()
    if (visiveis.isEmpty() || n == 0 || viewport <= 0f) return
    if (!state.canScrollForward && !state.canScrollBackward) return
    val scope = rememberCoroutineScope()
    // Itens têm alturas diferentes: estima o total pela média dos visíveis.
    val media = visiveis.sumOf { it.size }.toFloat() / visiveis.size
    val total = max(media * n, viewport + 1f)
    BarraRolagem(
        total = total,
        viewport = viewport,
        inicio = state.firstVisibleItemIndex * media + state.firstVisibleItemScrollOffset,
        rolar = { state.dispatchRawDelta(it) }
    )
    if (botoes && total > viewport * 1.5f) {
        BotoesTopoFim(
            podeSubir = state.canScrollBackward,
            podeDescer = state.canScrollForward,
            onTopo = { scope.launch { state.scrollToItem(0) } },
            onFim = { scope.launch { state.scrollToItem(max(0, n - 1)) } }
        )
    }
}

@Composable
private fun BoxScope.BarraRolagem(total: Float, viewport: Float, inicio: Float, rolar: (Float) -> Unit) {
    val density = LocalDensity.current
    var trilho by remember { mutableIntStateOf(0) }
    var arrastando by remember { mutableStateOf(false) }
    val minimo = with(density) { 36.dp.toPx() }
    val alturaThumb = if (trilho > 0) max(trilho * (viewport / total), minimo).coerceAtMost(trilho.toFloat()) else 0f
    val curso = (trilho - alturaThumb).coerceAtLeast(1f)
    val fator by rememberUpdatedState((total - viewport) / curso)
    val rolarAtual by rememberUpdatedState(rolar)
    Box(
        Modifier
            .align(Alignment.CenterEnd)
            .fillMaxHeight()
            .width(12.dp)
            .padding(vertical = 4.dp, horizontal = 2.dp)
            .clip(RoundedCornerShape(50))
            .background(Color(0x0F000000))
            .onSizeChanged { trilho = it.height }
            .pointerInput(Unit) {
                detectVerticalDragGestures(
                    onDragStart = { arrastando = true },
                    onDragEnd = { arrastando = false },
                    onDragCancel = { arrastando = false }
                ) { change, dy ->
                    change.consume()
                    rolarAtual(dy * fator)
                }
            }
    ) {
        if (trilho > 0) {
            val progresso = (inicio / (total - viewport).coerceAtLeast(1f)).coerceIn(0f, 1f)
            Box(
                Modifier
                    .offset { IntOffset(0, (curso * progresso).roundToInt()) }
                    .fillMaxWidth()
                    .height(with(density) { alturaThumb.toDp() })
                    .clip(RoundedCornerShape(50))
                    .background(PortalCores.Navy.copy(alpha = if (arrastando) 0.65f else 0.35f))
            )
        }
    }
}

@Composable
private fun BoxScope.BotoesTopoFim(podeSubir: Boolean, podeDescer: Boolean, onTopo: () -> Unit, onFim: () -> Unit) {
    if (!podeSubir && !podeDescer) return
    Column(
        Modifier.align(Alignment.BottomEnd).padding(end = 20.dp, bottom = 16.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        if (podeSubir) BotaoRolagem(Icons.Filled.KeyboardArrowUp, "Ir para o topo", onTopo)
        if (podeDescer) BotaoRolagem(Icons.Filled.KeyboardArrowDown, "Ir para o fim", onFim)
    }
}

@Composable
private fun BotaoRolagem(icone: ImageVector, descricao: String, onClick: () -> Unit) {
    Box(
        Modifier
            .size(40.dp)
            .shadow(4.dp, CircleShape)
            .clip(CircleShape)
            .background(PortalCores.Navy.copy(alpha = 0.88f))
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        Icon(icone, contentDescription = descricao, tint = Color.White)
    }
}
